<script lang="ts">
    import * as Sentry from "@sentry/svelte";
    import { AvailabilityStatus } from "@workadventure/messages";
    import { setContext } from "svelte";
    import type { Readable } from "svelte/store";
    import { derived, get } from "svelte/store";
    import { analyticsClient } from "../../../Administration/AnalyticsClient";
    import { connectionManager } from "../../../Connection/ConnectionManager";
    import { ENABLE_OPENID, SENTRY_DSN_FRONT } from "../../../Enum/EnvironmentVariable";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import { EnableCameraScene, EnableCameraSceneName } from "../../../Phaser/Login/EnableCameraScene";
    import { LoginScene, LoginSceneName } from "../../../Phaser/Login/LoginScene";
    import { PwaInstallScene, PwaInstallSceneName } from "../../../Phaser/Login/PwaInstallScene";
    import { SelectCharacterScene, SelectCharacterSceneName } from "../../../Phaser/Login/SelectCharacterScene";
    import { SelectCompanionScene, SelectCompanionSceneName } from "../../../Phaser/Login/SelectCompanionScene";
    import type { RequestedStatus } from "../../../Rules/StatusRules/statusRules";
    import { loginSceneVisibleStore } from "../../../Stores/LoginSceneStore";
    import { enableCameraSceneVisibilityStore } from "../../../Stores/MediaStore";
    import type { RightMenuItem } from "../../../Stores/MenuStore";
    import {
        openedMenuStore,
        rightActionBarMenuItems,
        showMenuItem,
        SubMenusInterface,
        userIsConnected,
    } from "../../../Stores/MenuStore";
    import { onboardingStore } from "../../../Stores/OnboardingStore";
    import { pwaInstallProfileMenuEligibleStore } from "../../../Stores/PwaInstallStore";
    import { selectCharacterSceneVisibleStore } from "../../../Stores/SelectCharacterStore";
    import { selectCompanionSceneVisibleStore } from "../../../Stores/SelectCompanionStore";
    import { getStatusInformation } from "../../../Utils/AvailabilityStatus";
    import { applySentryFeedbackTheme } from "../../../Utils/SentryFeedbackTheme";
    import { LL } from "../../../../i18n/i18n-svelte";
    import ActionBarButton from "../ActionBarButton.svelte";
    import AvailabilityStatusList from "../AvailabilityStatus/AvailabilityStatusList.svelte";
    import ExternalComponents from "../../ExternalModules/ExternalComponents.svelte";
    import CamSettingsIcon from "../../Icons/meta/CamSettingsIcon.svelte";
    import { IconFileDownload } from "../../Icons";
    import BugIcon from "../../Icons/meta/BugIcon.svelte";
    import HelpIcon from "../../Icons/meta/HelpIcon.svelte";
    import AvatarChangeIcon from "../../Icons/meta/AvatarChangeIcon.svelte";
    import CompanionChangeIcon from "../../Icons/meta/CompanionChangeIcon.svelte";
    import IconLogout from "../../Icons/meta/IconLogout.svelte";
    import ProfilIcon from "../../Icons/meta/ProfilIcon.svelte";
    import SettingsIcon from "../../Icons/meta/SettingsIcon.svelte";
    import AdditionalMenuItems from "./AdditionalMenuItems.svelte";
    import AppsMenuItem from "./AppsMenuItem.svelte";
    import ContextualMenuItems from "./ContextualMenuItems.svelte";
    import InviteMenuItem from "./InviteMenuItem.svelte";
    import LoginMenuItem from "./LoginMenuItem.svelte";
    import MapSubMenu from "./MapSubMenu.svelte";
    import ProfileMenuAccordeon from "./ProfileMenuAccordeon.svelte";

    interface Props {
        showContextualMenuItems: boolean;
    }

    let { showContextualMenuItems }: Props = $props();

    // This component is mounted in the global floating UI layer, so it must provide
    // the menu contexts itself rather than inheriting them from the action bar.
    setContext("profileMenu", true);
    setContext("inMenu", true);

    const statusToShow: Array<RequestedStatus | AvailabilityStatus.ONLINE> = [
        AvailabilityStatus.ONLINE,
        AvailabilityStatus.BUSY,
        AvailabilityStatus.BACK_IN_A_MOMENT,
        AvailabilityStatus.DO_NOT_DISTURB,
    ];

    function showWokaNameMenuItem() {
        return connectionManager.currentRoom?.opidWokaNamePolicy !== "force_opid";
    }

    function openEditNameScene() {
        loginSceneVisibleStore.set(true);
        gameManager.leaveGame(LoginSceneName, new LoginScene());
    }

    function openEditSkinScene() {
        selectCharacterSceneVisibleStore.set(true);
        gameManager.leaveGame(SelectCharacterSceneName, new SelectCharacterScene());
    }

    function openEditCompanionScene() {
        selectCompanionSceneVisibleStore.set(true);
        gameManager.leaveGame(SelectCompanionSceneName, new SelectCompanionScene());
    }

    function openEnableCameraScene() {
        enableCameraSceneVisibilityStore.showEnableCameraScene();
        gameManager.leaveGame(EnableCameraSceneName, new EnableCameraScene());
        analyticsClient.editCamera();
    }

    async function openFeedbackScene() {
        const feedbackIntegrationInstance = Sentry.feedbackIntegration({
            colorScheme: "light",
            showBranding: false,
            enableScreenshot: true,
            formTitle: $LL.actionbar.issueReport.formTitle(),
            emailLabel: $LL.actionbar.issueReport.emailLabel(),
            nameLabel: $LL.actionbar.issueReport.nameLabel(),
            messageLabel: $LL.actionbar.issueReport.descriptionLabel(),
            messagePlaceholder: $LL.actionbar.issueReport.descriptionPlaceholder(),
            submitButtonLabel: $LL.actionbar.issueReport.submitButtonLabel(),
            cancelButtonLabel: $LL.actionbar.issueReport.cancelButtonLabel(),
            confirmButtonLabel: $LL.actionbar.issueReport.confirmButtonLabel(),
            addScreenshotButtonLabel: $LL.actionbar.issueReport.addScreenshotButtonLabel(),
            removeScreenshotButtonLabel: $LL.actionbar.issueReport.removeScreenshotButtonLabel(),
            successMessageText: $LL.actionbar.issueReport.successMessageText(),
            removeHighlightText: $LL.actionbar.issueReport.removeHighlightText(),
            highlightToolText: $LL.actionbar.issueReport.highlightToolText(),
            hideToolText: $LL.actionbar.issueReport.hideToolText(),
            isRequiredLabel: "",
            onFormOpen: () => {
                gameManager.getCurrentGameScene().userInputManager.disableControls("store");
                gameManager.getCurrentGameScene().userInputManager.disableRightClick();
                openedMenuStore.close("profileMenu");
            },
            onFormClose: () => {
                gameManager.getCurrentGameScene().userInputManager.restoreControls("store");
                gameManager.getCurrentGameScene().userInputManager.restoreRightClick();
                teardownFeedbackForm();
            },
            onSubmitSuccess: () => {
                gameManager.getCurrentGameScene().userInputManager.restoreControls("store");
                gameManager.getCurrentGameScene().userInputManager.restoreRightClick();
            },
            // Fired after the 5s "thank you" toast, so tearing down here doesn't cut the toast short.
            onFormSubmitted: () => {
                teardownFeedbackForm();
            },
        });

        // Sentry only hides the dialog on close and, with a manual createForm(), never calls
        // removeFromDom(). Every feedbackIntegration() call also creates its own
        // `<div id="sentry-feedback">` host in <body>, and the integration's own remove() fails to
        // delete it: it does `_shadow.parentElement?.remove()` on a ShadowRoot, whose parentElement
        // is always null (the host is `shadowRoot.host`). So we remove the host ourselves.
        function teardownFeedbackForm(): void {
            if (!form) {
                return;
            }
            const shadowRoot = form.el instanceof Node ? form.el.getRootNode() : undefined;
            form.close();
            form.removeFromDom();
            feedbackIntegrationInstance.remove();
            if (shadowRoot instanceof ShadowRoot) {
                shadowRoot.host.remove();
            }
        }

        const form = await feedbackIntegrationInstance?.createForm();
        if (!form) {
            return;
        }
        form.appendToDom();
        if (form.el instanceof Node) {
            applySentryFeedbackTheme(form.el.getRootNode());
        }
        form.open();
    }

    let rightActionBarMenuItemsInBurgerMenu: Readable<RightMenuItem[]> = derived(
        rightActionBarMenuItems,
        ($rightActionBarMenuItems, set) => {
            const theDerived = derived(
                $rightActionBarMenuItems.map((item) => item.fallsInBurgerMenuStore),
                (items) => items,
            );
            const ghostSubscriptionUnsubscribe = theDerived.subscribe(() => {
                set(get(rightActionBarMenuItems).filter((item) => get(item.fallsInBurgerMenuStore)));
            });
            return () => ghostSubscriptionUnsubscribe();
        },
    );

    function openPwaInstallPrompt(): void {
        analyticsClient.pwaInstallFromProfileMenuClick();
        openedMenuStore.close("profileMenu");
        gameManager.leaveGame(PwaInstallSceneName, new PwaInstallScene());
    }
</script>

<div
    class="bg-mlight/80 shadow-light rounded-md2 backdrop-blur w-[258px] text-mdark select-none pointer-events-auto"
    data-testid="profile-menu"
>
    <div class="list-none overflow-y-auto max-h-[calc(100vh-180px)] narrow:max-h-[calc(100vh-60px)] px-6 py-5">
        <ExternalComponents zone="menuTop" />
        <AvailabilityStatusList statusInformation={getStatusInformation(statusToShow)} />

        {#if showContextualMenuItems}
            <div class="hidden narrow:block mt-2">
                <ContextualMenuItems />
            </div>
        {/if}

        <ProfileMenuAccordeon label={$LL.menu.sub.profile()} displayItems={true}>
            {#if showWokaNameMenuItem()}
                <ActionBarButton
                    label={$LL.actionbar.profil()}
                    onclick={() => {
                        openEditNameScene();
                        analyticsClient.editName();
                    }}
                    ariaLabel={$LL.actionbar.profil()}
                >
                    <ProfilIcon />
                </ActionBarButton>
            {/if}
            <ActionBarButton
                label={$LL.actionbar.woka()}
                onclick={() => {
                    openEditSkinScene();
                    analyticsClient.editWoka();
                }}
                ariaLabel={$LL.actionbar.woka()}
            >
                <AvatarChangeIcon />
            </ActionBarButton>
            <ActionBarButton
                label={$LL.actionbar.companion()}
                onclick={() => {
                    openEditCompanionScene();
                    analyticsClient.editCompanion();
                }}
                ariaLabel={$LL.actionbar.companion()}
            >
                <CompanionChangeIcon />
            </ActionBarButton>
        </ProfileMenuAccordeon>

        <ProfileMenuAccordeon label={$LL.menu.sub.settings()}>
            <ActionBarButton
                label={$LL.actionbar.editCamMic()}
                onclick={openEnableCameraScene}
                ariaLabel={$LL.actionbar.editCamMic()}
            >
                <CamSettingsIcon />
            </ActionBarButton>

            <ActionBarButton
                label={$LL.actionbar.allSettings()}
                onclick={() => {
                    showMenuItem(SubMenusInterface.settings);
                    analyticsClient.openedMenu();
                    openedMenuStore.close("profileMenu");
                }}
                ariaLabel={$LL.actionbar.allSettings()}
            >
                <SettingsIcon />
            </ActionBarButton>

            <AdditionalMenuItems menu="profileMenu" />

            {#each $rightActionBarMenuItemsInBurgerMenu ?? [] as button (button.id)}
                {@const ButtonComponent = button.component}
                <ButtonComponent {...button.props} />
            {/each}

            <AppsMenuItem />

            {#if $pwaInstallProfileMenuEligibleStore === true}
                <button
                    data-testid="profile-menu-install-web-app"
                    disabled={$pwaInstallProfileMenuEligibleStore !== true}
                    onclick={openPwaInstallPrompt}
                    class="group flex gap-3 px-0 py-1 m-0 items-center hover:bg-mlight-darker transition-all font-medium text-base w-full pointer-events-auto text-start rounded select-none text-mdark {$pwaInstallProfileMenuEligibleStore ===
                    true
                        ? 'cursor-pointer'
                        : 'opacity-50 cursor-not-allowed'}"
                >
                    <div class="transition-all w-5 h-5 aspect-square text-center flex items-center justify-center">
                        <IconFileDownload height="20" width="20" class="text-mdark" />
                    </div>
                    <div class="text-start leading-4 flex items-center text-nowrap">
                        {$LL.actionbar.installPwa()}
                    </div>
                </button>
            {/if}
        </ProfileMenuAccordeon>

        <MapSubMenu />

        <ProfileMenuAccordeon label="Помощь и советы">
            <ActionBarButton
                label={$LL.menu.profile.helpAndTips()}
                onclick={() => {
                    onboardingStore.restart();
                    openedMenuStore.close("profileMenu");
                }}
                ariaLabel={$LL.menu.profile.helpAndTips()}
            >
                <HelpIcon />
            </ActionBarButton>

            {#if SENTRY_DSN_FRONT != undefined && connectionManager.currentRoom?.isIssueReportEnabled}
                <ActionBarButton
                    label={$LL.actionbar.issueReport.menuAction()}
                    onclick={openFeedbackScene}
                    ariaLabel={$LL.actionbar.issueReport.menuAction()}
                >
                    <BugIcon />
                </ActionBarButton>
            {/if}
            <InviteMenuItem />
        </ProfileMenuAccordeon>

        {#if !(ENABLE_OPENID && $userIsConnected)}
            <LoginMenuItem classList="group w-fit hidden narrow:flex" />
        {/if}
        {#if ENABLE_OPENID && $userIsConnected}
            <ActionBarButton
                state="active"
                onclick={() => {
                    analyticsClient.logout();
                    connectionManager.logout();
                }}
                label={$LL.menu.profile.logout()}
                classList="hidden narrow:flex"
                ariaLabel={$LL.menu.profile.logout()}
                dataTestId="logout"
            >
                <IconLogout />
            </ActionBarButton>
        {/if}
    </div>
</div>
